import React, { useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import { downloadFile } from '../../../utils/fileDownload';
import { ATTACHMENT_ACCEPT, validateAttachmentFile } from '../../../utils/uploadValidation';
import { useChangeAttachments } from '../hooks/useChangeAttachments';

interface Props {
  changeId: number;
  active: boolean;
  canUpload?: boolean;
}

const ChangeEvidenceTab: React.FC<Props> = ({ changeId, active, canUpload = true }) => {
  const { attachments, reload, error: loadError } = useChangeAttachments(changeId, active);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // O endpoint de download exige `Authorization: Bearer`, que só existe no
  // interceptor do axios. Um `<a href>` navega sem header nenhum e leva 401,
  // por isso o download passa pelo helper autenticado como nos outros módulos.
  const handleDownload = (filePath: string, filename: string) => {
    setError(null);
    void downloadFile(filePath, filename, {
      onError: (message) => setError(message),
      onSessionExpired: () => setError('Sessão expirada. Faça login novamente.'),
      onFileMissing: (payload) =>
        setError(payload.detail || 'O arquivo não foi encontrado no armazenamento.'),
    });
  };

  const handleSelectFile = (file: File | null) => {
    setSelectedFile(file);
    setError(file ? validateAttachmentFile(file) : null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const validationError = validateAttachmentFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('description', uploadDescription);
    try {
      await changesApi.uploadAttachment(changeId, formData);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao enviar o anexo.'));
      return;
    }
    setSelectedFile(null);
    setUploadDescription('');
    await reload();
  };

  if (!active) return null;

  return (
    <Card className="mb-3">
      <Card.Body>
        {canUpload ? (
        <Row className="g-2 mb-3">
          <Col md={5}>
            <Form.Control
              type="file"
              accept={ATTACHMENT_ACCEPT}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSelectFile(e.target.files?.[0] || null)
              }
            />
          </Col>
          <Col md={5}>
            <Form.Control
              placeholder="Descrição do anexo"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Button className="w-100" onClick={() => void handleUpload()} disabled={!selectedFile}>
              Enviar
            </Button>
          </Col>
        </Row>
        ) : (
          <p className="text-muted small">Esta mudança está cancelada. Os anexos ficam só para consulta.</p>
        )}
        {loadError ? (
          <Alert variant="danger" className="py-2">
            {loadError}
          </Alert>
        ) : null}
        {error ? (
          <Alert variant="danger" className="py-2" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center"
          >
            <div>
              <strong>{attachment.filename}</strong>
              <div className="text-muted small">
                {attachment.description || 'Sem descrição'} •{' '}
                {new Date(attachment.uploaded_at).toLocaleString('pt-BR')}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handleDownload(attachment.file_path, attachment.filename)}
            >
              Download
            </Button>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

export default ChangeEvidenceTab;
